import { useEffect, useRef, useState, type ReactNode } from 'react';
import { copyToClipboard } from '../../lib/clipboard';

// P2b-3 §5 + round-1 polish Fix 2: click-to-copy text cell for non-URL
// paste targets (resolved Meta name, resolved Google ad label).
//
// Behaviour:
//   - Whole cell is the click target. Clicking the text OR the copy
//     icon copies `value`.
//   - On hover, a small copy icon appears at the right.
//   - "Copied!" tooltip flashes for ~1.5s after a successful copy. On
//     legacy-fallback failure (no clipboard API, no execCommand), the
//     tooltip says "Copy failed" instead.
//
// For URL fields where clicking the text should OPEN the URL (asset
// folder, landing URL, ad-account URL), use CopyableLinkCell instead.
//
// Empty/null `value` collapses to a non-interactive em-dash placeholder.

type Props = {
  value: string | null | undefined;
  display?: ReactNode; // defaults to value
  ariaLabel: string;
  // Optional Tailwind width class (the role grids size cells per column);
  // defaults to no fixed width.
  className?: string;
};

const TOAST_MS = 1500;

export function CopyableCell({ value, display, ariaLabel, className }: Props) {
  const [feedback, setFeedback] = useState<'idle' | 'copied' | 'error'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  if (typeof value !== 'string' || value.length === 0) {
    return <span className="text-slate-400">—</span>;
  }

  const handleCopy = async () => {
    const ok = await copyToClipboard(value);
    setFeedback(ok ? 'copied' : 'error');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setFeedback('idle'), TOAST_MS);
  };

  return (
    <span className={`relative inline-flex items-center gap-1 group ${className ?? ''}`}>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={ariaLabel}
        title={value}
        className="text-left text-xs hover:underline focus:outline-none focus:ring-1 focus:ring-slate-400 rounded truncate min-w-0 max-w-full"
      >
        {display ?? value}
      </button>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={`Copy ${ariaLabel}`}
        title="Copy"
        className="text-slate-400 hover:text-slate-700 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {/* Plain unicode glyph keeps the bundle free of an icon dep. */}
        ⎘
      </button>
      {feedback !== 'idle' && (
        <span
          role="status"
          aria-live="polite"
          className={
            'absolute -top-6 left-0 px-2 py-0.5 text-xs rounded shadow-md whitespace-nowrap ' +
            (feedback === 'copied'
              ? 'bg-emerald-600 text-white'
              : 'bg-rose-600 text-white')
          }
        >
          {feedback === 'copied' ? 'Copied!' : 'Copy failed'}
        </span>
      )}
    </span>
  );
}
